import React from "react";
import * as Sentry from "@sentry/react";

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the component tree and:
 * 1. Reports them to Sentry for monitoring
 * 2. Displays a fallback UI instead of crashing the entire app
 *
 * This component wraps the entire application to catch unhandled errors
 * that would otherwise crash the React tree.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 * @param {Object} props Component props.
 * @param {React.ReactNode} props.children Application tree to protect.
 */
class ErrorBoundary extends React.Component {
  /**
   * @param {Object} props Component props.
   */
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  /**
   * Flags the next render to show the fallback UI.
   *
   * @param {Error} error Caught render error.
   * @returns {{hasError: boolean}} Partial state update.
   */
  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  /**
   * Reports the error to Sentry and stores details for the fallback UI.
   *
   * @param {Error} error Caught error.
   * @param {Object} errorInfo React component stack info.
   * @returns {void}
   */
  componentDidCatch(error, errorInfo) {
    // Log error details to console for debugging
    console.error("Error caught by ErrorBoundary:", error, errorInfo);

    // Report to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    // Update state with error details for display
    this.setState({
      error,
      errorInfo
    });
  }

  /**
   * Clears the error state and reloads the page.
   *
   * @returns {void}
   */
  handleReload = () => {
    // Clear error state and reload
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  /**
   * Renders children, or the fallback UI when an error was caught.
   *
   * @returns {React.ReactNode} Children or fallback.
   */
  render() {
    if (this.state.hasError) {
      // Fallback UI when error occurs
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          fontFamily: 'Inter, system-ui, sans-serif',
          backgroundColor: '#f5f5f5'
        }}>
          <div style={{
            maxWidth: '600px',
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h1 style={{
              color: '#d32f2f',
              fontSize: '24px',
              marginBottom: '16px'
            }}>
              Something went wrong
            </h1>

            <p style={{
              color: '#666',
              fontSize: '16px',
              lineHeight: '1.5',
              marginBottom: '24px'
            }}>
              We're sorry, but an unexpected error occurred. The error has been reported
              to our team and we'll look into it as soon as possible.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                textAlign: 'left',
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  Error Details (Development Only)
                </summary>
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '12px',
                  color: '#d32f2f'
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <button
              onClick={this.handleReload}
              style={{
                backgroundColor: '#1976d2',
                color: 'white',
                padding: '12px 32px',
                fontSize: '16px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#1565c0'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#1976d2'}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
